import { message } from "antd";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import {
  mergeMap,
  map,
  catchError,
  filter,
  tap,
  mergeAll,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import {
  ExtractApi,
  WorkflowInfo,
  WorkflowLog,
} from "@/features/file-management/pages/FilePage/services/extractionFile.service";
import { from, of } from "rxjs";
import { ConversionApi, TaskLog } from "@/api/conversion.api";
import {
  selectJobsFileStatusList,
  uploadFileActions,
  type UploadFileStatus1,
} from "@/features/upload/stores/uploadFiles/uploadFileSlice";
import { FileInfoResponse } from "@/common";
import { RootState } from "@/store/types";
import { extractionActions } from "@/features/file-management/pages/FilePage/redux/extraction.slice";
import { EMPTY } from "rxjs";

export interface WorkflowStatus {
  workflowId: string;
  filePath: string | null;
  currentStep: number;
  stepCount: number;
  completedAt: string;
  durationSeconds: number;
  stressTestMode: boolean;
}
export interface LogData {
  durationSeconds: number | null;
  exchange: string;
  routingKey: string;
  stage: string | null;
  status: string;
  stressTest: string | null;
  timestamp: string;
  workflowId: string;
  message: string | null;
  data: WorkflowStatus;
}
export interface LogDataWithMeta extends LogData {
  isNew?: boolean;
}
export interface FileTaskStatus {
  id: string;
  status: number;
  startTime: string;
  endTime: string;
  fileId: string;
  baseFileId: string;
  currentStep: number;
  numberStep: number;
  currentStepName: string;
}

export interface TaskLogWithMeta extends TaskLog {
  isNew?: boolean;
}

export interface fileStatusState {
  task: Record<string, FileTaskStatus>; // <fileId> -> FileTaskStatus
  logs: Record<string, TaskLogWithMeta[]>; // taskId -> TaskLog[]
  logData: LogDataWithMeta[]; // realtime SignalR
  displayLogs: Record<string, LogDataWithMeta[]>; // <fileId> -> logs UI
  fileWorkflowMap: Record<string, string[]>; // <fileId> -> workflowId
  latestLogs: Record<string, LogDataWithMeta[]>; // <fileId> -> latest logs for other functions
  workflowLogs: Record<string, LogDataWithMeta[]>; // <fileId> -> logs từ workflow SignalR
  taskLogs: Record<string, LogDataWithMeta[]>; // <fileId> -> logs từ taskId converting SignalR
  loading: boolean;
  logLoading: boolean;
  error?: string;
  logModalState: Record<
    string,
    {
      isLoading: boolean;
      error: string | null;
    }
  >;
  statusByFileInfo: Record<string, FileInfoResponse>;
  extractedFiles: string[]; // Track files that have already been extracted
}

const initialState: fileStatusState = {
  task: {},
  logs: {},
  logData: [],
  displayLogs: {},
  fileWorkflowMap: {},
  latestLogs: {},
  workflowLogs: {},
  taskLogs: {},
  loading: false,
  logLoading: false,
  logModalState: {},
  statusByFileInfo: {},
  extractedFiles: [],
};

// ---- Slice ----
const fileStatusSlice = createSlice({
  name: "FileStatus",
  initialState,
  reducers: {
    getFileStatus: (
      state,
      _action: PayloadAction<{
        baseFileId: string;
        name?: string;
        size?: number;
      }>
    ) => {
      state.loading = true;
      state.error = undefined;
    },

    getTaskByBaseFileId: (
      state,
      _action: PayloadAction<{
        baseFileId: string;
        name?: string;
        size?: number;
      }>
    ) => {
      state.loading = true;
      state.error = undefined;
    },

    fileStatusFetched: (
      state,
      action: PayloadAction<{ baseFileId: string; data: FileTaskStatus }>
    ) => {
      state.task[action.payload.baseFileId] = action.payload.data;
      state.loading = false;
    },

    fileStatusFailed: (
      state,
      action: PayloadAction<{ baseFileId: string; error: string }>
    ) => {
      state.loading = false;
      state.error = action.payload.error;
    },

    clearFileStatus: (state, action: PayloadAction<{ baseFileId: string }>) => {
      delete state.task[action.payload.baseFileId];
    },

    getTaskLogByTaskId: (state, _action: PayloadAction<{ taskId: string }>) => {
      state.loading = true;
      state.error = undefined;
    },

    taskLogFetched: (
      state,
      action: PayloadAction<{ taskId: string; logs: TaskLog[] }>
    ) => {
      const taskId = action.payload.taskId;
      const currentLogs = state.logs[taskId] || [];
      const existingKeys = new Set(
        currentLogs.map((l) => `${l.id}-${l.createTime}-${l.log}`)
      );

      const newLogs = action.payload.logs.map((l) => ({
        ...l,
        isNew: !existingKeys.has(`${l.id}-${l.createTime}-${l.log}`),
      }));

      const merged = [...currentLogs, ...newLogs].filter(
        (log, index, self) =>
          index ===
          self.findIndex(
            (l) =>
              l.id === log.id &&
              l.createTime === log.createTime &&
              l.log === log.log
          )
      );

      merged.sort(
        (a, b) =>
          new Date(b.createTime).getTime() - new Date(a.createTime).getTime()
      );

      state.logs[taskId] = merged;
      state.logLoading = false;
    },

    taskLogFailed: (
      state,
      action: PayloadAction<{ taskId: string; error: string }>
    ) => {
      state.loading = false;
      state.error = action.payload.error;
    },

    clearTaskLog: (state, action: PayloadAction<{ taskId: string }>) => {
      delete state.logs[action.payload.taskId];
    },
    clearNewFlag: (state, action: PayloadAction<{ taskId: string }>) => {
      const logs = state.logs[action.payload.taskId];
      if (logs) {
        state.logs[action.payload.taskId] = logs.map((l) => ({
          ...l,
          isNew: false,
        }));
      }
    },
    // --- LogData từ SignalR ---
    addLogData: (state, action: PayloadAction<LogDataWithMeta>) => {
      const log = action.payload;
      state.logData.unshift(log);

      const { workflowId } = log;
      let isWorkflowLog = false;
      let isTaskLog = false;

     // Kiểm tra workflow mapping hiện tại
      for (const fileId in state.fileWorkflowMap) {
        const fileWorkflowIds = state.fileWorkflowMap[fileId] || [];
        if (fileWorkflowIds.includes(workflowId)) {
          // Cập nhật displayLogs và latestLogs
          if (!state.displayLogs[fileId]) {
            state.displayLogs[fileId] = [];
          }
          state.displayLogs[fileId] = [log, ...state.displayLogs[fileId]];
          state.latestLogs[fileId] = [log, ...(state.latestLogs[fileId] || [])];

          // Kiểm tra xem có phải là task conversion log không
          const taskStatus = state.task[fileId];
          if (taskStatus && taskStatus.id === workflowId) {
            // Đây là log từ conversion task
            if (!state.taskLogs[fileId]) {
              state.taskLogs[fileId] = [];
            }
            const taskLogExists = state.taskLogs[fileId].some(
              existingLog => existingLog.timestamp === log.timestamp &&
                existingLog.workflowId === log.workflowId
            );
            if (!taskLogExists) {
              state.taskLogs[fileId] = [log, ...state.taskLogs[fileId]];
            }
            isTaskLog = true;
          } else {
            // Đây là log từ extraction workflow
            if (!state.workflowLogs[fileId]) {
              state.workflowLogs[fileId] = [];
            }
            const workflowLogExists = state.workflowLogs[fileId].some(
              existingLog => existingLog.timestamp === log.timestamp &&
                existingLog.workflowId === log.workflowId
            );
            if (!workflowLogExists) {
              state.workflowLogs[fileId] = [log, ...state.workflowLogs[fileId]];
            }
            isWorkflowLog = true;
          }
          break; // Found the file, no need to continue
        }
      }

      // Nếu không tìm thấy trong mapping hiện tại, kiểm tra taskId từ converting files
      if (!isWorkflowLog && !isTaskLog) {
        for (const fileId in state.task) {
          const taskStatus = state.task[fileId];
          if (taskStatus && taskStatus.id === workflowId) {
            // Thêm vào mapping
            if (!state.fileWorkflowMap[fileId]) {
              state.fileWorkflowMap[fileId] = [];
            }
            if (!state.fileWorkflowMap[fileId].includes(workflowId)) {
              state.fileWorkflowMap[fileId].push(workflowId);
            }

            if (!state.displayLogs[fileId]) {
              state.displayLogs[fileId] = [];
            }
            const logExists = state.displayLogs[fileId].some(
              existingLog => existingLog.timestamp === log.timestamp &&
                existingLog.workflowId === log.workflowId
            );
            if (!logExists) {
              state.displayLogs[fileId] = [log, ...state.displayLogs[fileId]];
              state.latestLogs[fileId] = [log, ...(state.latestLogs[fileId] || [])];
            }

            // Thêm vào taskLogs riêng
            if (!state.taskLogs[fileId]) {
              state.taskLogs[fileId] = [];
            }
            const taskLogExists = state.taskLogs[fileId].some(
              existingLog => existingLog.timestamp === log.timestamp &&
                existingLog.workflowId === log.workflowId
            );
            if (!taskLogExists) {
              state.taskLogs[fileId] = [log, ...state.taskLogs[fileId]];
            }
            break;
          }
        }
      }
    },
    clearLogData: (state) => {
      state.logData = [];
    },
    markLogsAsRead: (state) => {
      state.logData = state.logData.map((log) => ({ ...log, isNew: false }));
    },
    startDisplayLogs: (
      state,
      action: PayloadAction<{ fileId: string; workflowIds: string[] }>
    ) => {
      const { fileId, workflowIds } = action.payload;

      // lưu mapping để addLogData biết workflow nào thuộc file này
      state.fileWorkflowMap[fileId] = workflowIds;

      // khởi tạo logs từ logData hiện tại
      state.displayLogs[fileId] = state.logData.filter((l) =>
        workflowIds.includes(l.workflowId)
      );
    },
    clearDisplayLogsNewFlag: (
      state,
      action: PayloadAction<{ fileId: string }>
    ) => {
      const { fileId } = action.payload;
      const logs = state.displayLogs[fileId];
      if (logs) {
        state.displayLogs[fileId] = logs.map((l) => ({ ...l, isNew: false }));
      }
    },

    replaceLogsForFile: (
      state,
      action: PayloadAction<{ fileId: string; logs: LogDataWithMeta[] }>
    ) => {
      const { fileId, logs } = action.payload;
      state.displayLogs[fileId] = logs;
      // Update latestLogs for other functions to use
      state.latestLogs[fileId] = logs;

      // merge vào global logData (nếu cần, tránh trùng lặp)
      const existingIds = new Set(state.logData.map((l) => l.timestamp));
      const newLogs = logs.filter((l) => !existingIds.has(l.timestamp));
      state.logData.push(...newLogs);
    },

    stopDisplayLogs: (state, action: PayloadAction<{ fileId: string }>) => {
      delete state.logModalState[action.payload.fileId];
    },

    fetchLogsStart: (state, action: PayloadAction<{ fileId: string }>) => {
      state.logModalState[action.payload.fileId] = {
        isLoading: true,
        error: null,
      };
    },

    fetchLogsSuccess: (
      state,
      action: PayloadAction<{
        fileId: string;
        workflowIds: string[];
        logs: LogDataWithMeta[];
      }>
    ) => {
      const { fileId, workflowIds, logs } = action.payload;
      state.logModalState[fileId] = {
        isLoading: false,
        error: null,
      };
      state.fileWorkflowMap[fileId] = workflowIds;
      state.displayLogs[fileId] = logs;
      // Update latestLogs for other functions to use
      state.latestLogs[fileId] = logs;

      // Update workflowLogs vì đây là logs từ extraction workflow
      state.workflowLogs[fileId] = logs;

      // merge vào global logData (nếu cần, tránh trùng lặp)
      const existingIds = new Set(state.logData.map((l) => l.timestamp));
      const newLogs = logs.filter((l) => !existingIds.has(l.timestamp));
      state.logData.push(...newLogs);
    },

    fetchLogsFailed: (
      state,
      action: PayloadAction<{ fileId: string; error: string }>
    ) => {
      const { fileId, error } = action.payload;
      state.logModalState[fileId] = {
        isLoading: false,
        error,
      };
    },
    getFileInfoByBaseFilesRequest: (
      state,
      _action: PayloadAction<{ baseFileId: { baseFileId: string }[] }>
    ) => {
      state.loading = true;
    },
    getFileInfoByBaseFilesSuccess: (
      state,
      action: PayloadAction<FileInfoResponse[]>
    ) => {
      state.loading = false;
      const infoMap: Record<string, FileInfoResponse> = {};
      action.payload.forEach((info) => {
        if (info.baseFileId) {
          infoMap[String(info.baseFileId)] = info;
        }
      });
      state.statusByFileInfo = {
        ...state.statusByFileInfo,
        ...infoMap,
      };
    },
    getFileInfoByBaseFilesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    markFileAsExtracted: (
      state,
      action: PayloadAction<{ baseFileId: string }>
    ) => {
      const { baseFileId } = action.payload;
      if (!state.extractedFiles.includes(baseFileId)) {
        state.extractedFiles.push(baseFileId);
      }
    },
    // Latest logs management
    updateLatestLogs: (
      state,
      action: PayloadAction<{ fileId: string; logs: LogDataWithMeta[] }>
    ) => {
      const { fileId, logs } = action.payload;
      state.latestLogs[fileId] = logs;
    },

    clearLatestLogs: (
      state,
      action: PayloadAction<{ fileId: string }>
    ) => {
      const { fileId } = action.payload;
      delete state.latestLogs[fileId];
    },

    clearAllLatestLogs: (state) => {
      state.latestLogs = {};
    },

    clearWorkflowLogs: (
      state,
      action: PayloadAction<{ fileId: string }>
    ) => {
      const { fileId } = action.payload;
      delete state.workflowLogs[fileId];
    },

    clearAllWorkflowLogs: (state) => {
      state.workflowLogs = {};
    },

    clearTaskLogsForFile: (
      state,
      action: PayloadAction<{ fileId: string }>
    ) => {
      const { fileId } = action.payload;
      delete state.taskLogs[fileId];
    },

    saveExtractionWorkflowMapping: (
      state,
      action: PayloadAction<{ fileId: string; workflowId: string }>
    ) => {
      const { fileId, workflowId } = action.payload;

      const existing = state.fileWorkflowMap[fileId];

      if (!existing || existing.length === 0) {
        state.fileWorkflowMap[fileId] = [workflowId];
      }
      else if (existing[0] !== workflowId) {
        state.fileWorkflowMap[fileId] = [workflowId];
      }
    },
  },
});

// ---- Epics ----
export const getFileStatusEpic = (action$: any) =>
  action$.pipe(
    ofType(fileStatusActions.getFileStatus.type),
    mergeMap((action: ReturnType<typeof fileStatusActions.getFileStatus>) =>
      ConversionApi.getTaskByBaseFileId(action.payload.baseFileId).pipe(
        map((response: FileTaskStatus) => {
          const baseFileId = action.payload.baseFileId;
          // Trả về mảng 2 action
          return [
            fileStatusActions.fileStatusFetched({ baseFileId, data: response }),
            uploadFileActions.updateTaskStatus({ baseFileId, task: response }),
          ];
        }),
        mergeAll(), // flatten array thành 2 action riêng
        catchError((error) =>
          of(
            fileStatusActions.fileStatusFailed({
              baseFileId: action.payload.baseFileId,
              error: error.message ?? "Something went wrong",
            })
          )
        )
      )
    )
  );

export const getTaskLogByTaskIdEpic = (action$: any) =>
  action$.pipe(
    ofType(fileStatusActions.getTaskLogByTaskId.type),
    mergeMap((action: ReturnType<typeof fileStatusActions.getTaskLogByTaskId>) =>
      from(ConversionApi.getTaskLogByTaskId(action.payload.taskId)).pipe(
        map((taskLogs: TaskLog[]) => {
          console.log("[getTaskLogByTaskIdEpic] Got task logs:", taskLogs);

          const taskLogsArray = Array.isArray(taskLogs) ? taskLogs : [taskLogs];
          const latestTaskLogs = taskLogsArray
            .sort((a, b) => new Date(b.createTime).getTime() - new Date(a.createTime).getTime())
            .slice(0, 20);

          return fileStatusActions.taskLogFetched({
            taskId: action.payload.taskId,
            logs: latestTaskLogs,
          });
        }),
        catchError((error) => {
          console.error("[getTaskLogByTaskIdEpic] Error fetching task logs:", error);
          return of(
            fileStatusActions.taskLogFailed({
              taskId: action.payload.taskId,
              error: error.message ?? "Something went wrong",
            })
          );
        })
      )
    )
  );

export const fetchLogsEpic = (action$: any) =>
  action$.pipe(
    ofType(fileStatusActions.fetchLogsStart.type),
    mergeMap((action: ReturnType<typeof fileStatusActions.fetchLogsStart>) => {
      const fileId = action.payload.fileId;
      const projectName = localStorage.getItem("projectName");

      if (!projectName) {
        return of(
          fileStatusActions.fetchLogsFailed({
            fileId,
            error: "Project name not found",
          })
        );
      }

      return from(
        ExtractApi.Get.getWorkflowByFileId({
          projectName,
          fileId: Number(fileId),
        })
      ).pipe(
        mergeMap((workflows) => {
          const workflowIdsRes = (workflows ?? []).map((w) => w.workflowId);
          const workflowIds =
            workflowIdsRes.length > 0 ? [workflowIdsRes[0]] : [];

          if (workflowIds.length === 0) {
            return of(
              fileStatusActions.fetchLogsFailed({
                fileId,
                error: "No workflows found for this file",
              })
            );
          }

          return ExtractApi.Post.logsByWorkflow({
            workflowIds,
          }).pipe(
            map((response) => {
              const MAX_LOGS = 50;
              const logs = Object.values(response.groupedLogs || {})
                .flat()
                .sort(
                  (a, b) =>
                    new Date(b.Timestamp).getTime() -
                    new Date(a.Timestamp).getTime()
                )
                .slice(0, MAX_LOGS)
                .map((log) => ({
                  id: log._id.$oid,
                  workflowId: log.WorkflowId,
                  timestamp: log.Timestamp,
                  status: log.Status,
                  stage: log.Stage,
                  durationSeconds: log.DurationSeconds,
                  stressTest: log.StressTest,
                  exchange: log.Exchange,
                  routingKey: log.RoutingKey,
                  message: log.Message,
                  project: log.project,
                  data: {
                    workflowId: log.WorkflowId,
                    filePath: null,
                    currentStep: 0,
                    stepCount: 0,
                    completedAt: log.Timestamp,
                    durationSeconds: log.DurationSeconds || 0,
                    stressTestMode: Boolean(log.StressTest),
                  },
                }));

              return fileStatusActions.fetchLogsSuccess({
                fileId,
                workflowIds,
                logs,
              });
            })
          );
        }),
        catchError((error) =>
          of(
            fileStatusActions.fetchLogsFailed({
              fileId,
              error: error.message || "Failed to fetch logs",
            })
          )
        )
      );
    })
  );

export const getFileInfoByBaseFilesEpic = (action$: any) =>
  action$.pipe(
    ofType(fileStatusActions.getFileInfoByBaseFilesRequest.type),
    mergeMap((action: any) => {
      const { baseFileId } = action.payload;
      return ConversionApi.getFileInfoByBaseFiles(baseFileId).pipe(
        map((response) =>
          fileStatusActions.getFileInfoByBaseFilesSuccess(response)
        ),
        catchError((error) =>
          of(fileStatusActions.getFileInfoByBaseFilesFailure(error.message))
        )
      );
    })
  );

export const getTaskByBaseFileId = (action$: any) =>
  action$.pipe(
    ofType(fileStatusActions.getTaskByBaseFileId.type),
    mergeMap(
      (action: ReturnType<typeof fileStatusActions.getTaskByBaseFileId>) =>
        ConversionApi.getTaskByBaseFileId(action.payload.baseFileId).pipe(
          map((response: FileTaskStatus) => {
            const baseFileId = action.payload.baseFileId;
            return [
              fileStatusActions.fileStatusFetched({
                baseFileId,
                data: response,
              }),
            ];
          }),
          mergeAll(),
          catchError((error) =>
            of(
              fileStatusActions.fileStatusFailed({
                baseFileId: action.payload.baseFileId,
                error: error.message ?? "Something went wrong",
              })
            )
          )
        )
    )
  );

export const fileStatusActions = fileStatusSlice.actions;
export default fileStatusSlice.reducer;

// Epic để tự động trigger extraction khi task hoàn thành (từ convertFile)
export const autoExtractMetadataFromConvertEpic = (action$: any, state$: any) =>
  action$.pipe(
    ofType(fileStatusActions.fileStatusFetched.type),
    withLatestFrom(state$),
    filter(([action, state]: [ReturnType<typeof fileStatusActions.fileStatusFetched>, RootState]) => {
      const task = action.payload?.data;
      const { baseFileId } = action.payload;

      // Kiểm tra xem file đã được extract chưa
      const alreadyExtracted =
        state.fileStatus.extractedFiles.includes(baseFileId);

      const fileRecord = state.uploadFile.fileStatusList.find(
        (f) => f.baseFileId === baseFileId
      );

      if (alreadyExtracted) {
        return false;
      }

      if (!fileRecord) {
        return false;
      }

      if (fileRecord.progress !== 100) {
        return false;
      }

      if (!task) {
        return false;
      }

      return true;
    }),
    mergeMap(
      ([action, state]: [ReturnType<typeof fileStatusActions.fileStatusFetched>, RootState]) => {
        const { baseFileId } = action.payload;

        // Tìm file record từ uploadModalFileStatusList
        const fileRecord = state.uploadFile.fileStatusList.find(
          (f) => f.baseFileId === baseFileId
        );

        if (!fileRecord || !fileRecord.file) {
          return EMPTY;
        }

        const fileName = fileRecord.file.name;
        const projectName = String(localStorage.getItem("projectName"));

        // Chỉ auto-extract cho các file Revit (.rvt)
        if (!fileName.toLowerCase().endsWith(".rvt")) {
          return EMPTY;
        }


        const payload = {
          project: projectName,
          revitFilePath: fileName,
          s3Key: `${projectName}/${fileName}`,
          databaseName: projectName,
          facility: "jedons_facility_1",
          fileId: baseFileId,
        };

        // Mark file as extracted và dispatch extraction request
        return of([
          fileStatusActions.markFileAsExtracted({ baseFileId }),
          extractionActions.extractMetadataRequest(payload),
        ]).pipe(mergeMap((actions) => actions));
      }
    ),
    catchError((error) => {
      console.error("[Convert Auto Extract] Error:", error);
      return EMPTY;
    })
  );

export const convertFileEpics = [
  getFileStatusEpic,
  getTaskLogByTaskIdEpic,
  fetchLogsEpic,
  getFileInfoByBaseFilesEpic,
  getTaskByBaseFileId,
  autoExtractMetadataFromConvertEpic,
];

export const selectMergedFileStatus = createSelector(
  (state: RootState) => state.fileStatus.task,
  (state: RootState) => state.fileStatus.workflowLogs,
  (state: RootState) => state.uploadFile.fileStatusList,
  (state: RootState) => state.file.fileIds, // fileIds RVT
  (task, displayLogs, uploadModalFiles, fileIds) => {
    const taskEntries = Object.entries(task as Record<string, FileTaskStatus>);

    const uploadModalEntries = uploadModalFiles
      .filter((file: UploadFileStatus1) => file.baseFileId)
      .map((file: UploadFileStatus1) => [file.baseFileId!, file.statusTask!]);

    const allEntries = [...taskEntries, ...uploadModalEntries] as [
      string,
      FileTaskStatus
    ][];

    const mergedData = allEntries.map(([fileId, fileTask]) => {
      const logs = displayLogs[fileId] ?? [];
      const firstLog = logs.length > 0 ? logs[0] : null;

      let statusFinal: number;
      const convertStatusCode = fileTask?.status;

      if (fileIds.map(String).includes(fileId)) {
        const extractStatusCode = firstLog?.status;

        if (!convertStatusCode && !extractStatusCode) {
          statusFinal = 0; // Not started
        } else if (
          convertStatusCode === 2 &&
          extractStatusCode === "Completed"
        ) {
          statusFinal = 2; // Success
        } else if (
          (convertStatusCode === 3 && extractStatusCode === "Completed") ||
          (convertStatusCode === 2 && extractStatusCode === "Failed") ||
          (convertStatusCode === 3 && extractStatusCode === "Failed")
        ) {
          statusFinal = 3; // Failed
        } else {
          statusFinal = 1; // Processing
        }
      } else {
        if (!convertStatusCode) {
          statusFinal = 0;
        } else {
          switch (convertStatusCode) {
            case 0:
              statusFinal = 0;
              break;
            case 1:
              statusFinal = 1;
              break;
            case 2:
              statusFinal = 2;
              break;
            case 3:
              statusFinal = 3;
              break;
            default:
              statusFinal = 0;
          }
        }
      }

      return {
        fileId,
        convertStatus: fileTask,
        extractStatus: firstLog,
        statusFinal,
      };
    });

    return mergedData;
  }
);

export const selectMergedJobsFileStatus = createSelector(
  [selectJobsFileStatusList, selectMergedFileStatus],
  (fileStatusList, mergedStatus) => {
    return fileStatusList.map((file: any) => {
      const fileId = String(file.baseFileId ?? "");
      const status = mergedStatus.find((m) => m.fileId === fileId);

      return {
        ...file,
        convertStatus: status?.convertStatus ?? null,
        extractStatus: status?.extractStatus ?? null,
        statusFinal: status?.statusFinal ?? null,
      };
    });
  }
);

// Selector mới để merge displayLogs với task logs cho modal
export const selectDisplayLogsWithTaskLogs = createSelector(
  (state: RootState) => state.fileStatus.displayLogs,
  (state: RootState) => state.fileStatus.logs,
  (state: RootState) => state.fileStatus.task,
  (displayLogs, taskLogs, taskStatus) => (fileId: string): LogDataWithMeta[] => {
    // Lấy displayLogs gốc (từ SignalR/workflow logs)
    const existingLogs = displayLogs[fileId] || [];

    // Tìm task status để lấy taskId
    const task = taskStatus[fileId];
    if (!task) {
      return existingLogs;
    }

    // Lấy task logs và convert thành LogDataWithMeta format
    const currentTaskLogs = taskLogs[task.id] || [];
    const convertedTaskLogs: LogDataWithMeta[] = currentTaskLogs.map((log: TaskLogWithMeta) => ({
      durationSeconds: null,
      exchange: "",
      routingKey: "",
      stage: null,
      status: "",
      stressTest: null,
      timestamp: log.createTime,
      workflowId: log.taskId || log.id,
      message: log.message || `${log.action ?? ""} ${log.type ?? ""}`
    }));

    // Merge và sort theo timestamp
    const mergedLogs = [...existingLogs, ...convertedTaskLogs]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return mergedLogs;
  }
);

export const selectConvertLogs = createSelector(
  (state: RootState) => state.fileStatus.logs,
  (state: RootState) => state.fileStatus.task,
  (taskLogs, taskStatus) => (fileId: string): LogDataWithMeta | undefined => {
    const task = taskStatus[fileId];
    if (!task) return undefined;

    const currentTaskLogs = taskLogs[task.id] || [];

    if (!currentTaskLogs.length) return undefined;

    const convertedLogs: LogDataWithMeta[] = currentTaskLogs.map(
      (log: TaskLogWithMeta) => ({
        durationSeconds: null,
        exchange: "",
        routingKey: "",
        stage: null,
        status: "",
        stressTest: null,
        timestamp: log.createTime,
        workflowId: log.taskId,
        message: log.message || `${log.action ?? ""} ${log.type ?? ""}`,
        log: log.log,
      })
    );

    const newestLog = convertedLogs.reduce((latest, current) =>
      new Date(current.timestamp).getTime() > new Date(latest.timestamp).getTime()
        ? current
        : latest
    );

    return newestLog;
  }
);

export const selectWorkflowLogsByFileId = createSelector(
  (state: RootState) => state.fileStatus.workflowLogs,
  (workflowLogs) => (fileId: string): LogDataWithMeta | {} => {
    const logs = workflowLogs[fileId] || [];
    if (logs.length === 0) return {};
    const newestLog = [...logs].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )[0];
    return newestLog;
  }
);

export const selectTaskLogsByFileId = (
  state: RootState,
  fileId: string
): LogDataWithMeta | null => {
  const logs = state.fileStatus.taskLogs[fileId];
  if (!logs || logs.length === 0) return null;

  return logs.reduce((latest: LogDataWithMeta, current: LogDataWithMeta) => {
    const latestTime = new Date(latest.timestamp).getTime();
    const currentTime = new Date(current.timestamp).getTime();
    return currentTime > latestTime ? current : latest;
  });
};

// Selector để lấy tất cả workflow logs
export const selectAllWorkflowLogs = createSelector(
  (state: RootState) => state.fileStatus.workflowLogs,
  (workflowLogs) => workflowLogs
);

// Selector để lấy tất cả task logs
export const selectAllTaskLogs = createSelector(
  (state: RootState) => state.fileStatus.taskLogs,
  (taskLogs) => taskLogs
);
